// Shared handler for every video modal in the theme (any `.oi-video-modal`).
//
// Replaces the per-template inline handlers that injected a bare
// <iframe src="youtube.com/embed/..."> on show.bs.modal. That markup carried none
// of the attributes oilib looks for and no `enablejsapi=1`, so `oi.video` never
// adopted the player and no VideoPlay / VideoProgress event was ever emitted --
// every video on the site was untracked.
//
// The markup built here mirrors oilib's own `{% video_iframe %}` output
// (oicms website/base2/inclusions/video-iframe.html) so `oi.video` picks it up on
// its own: its MutationObserver watches for an added node containing both a
// `.oi-video` element and a `[data-oi-video-autoload]` element, then mounts the
// vendor API and handles the GTM/HFA sends itself. We deliberately do NOT push any
// event from here -- doing so would double-count against oi.video.
//
// `data-oi-video-has` must include `controls`: oilib treats a video with
// autoplay+loop+mute and no controls as a background video and sets `track = false`,
// which would silently disable tracking.

const YOUTUBE_ALLOW = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
const VIMEO_ALLOW = "autoplay; fullscreen; picture-in-picture";

// Each open gets its own element id. oilib keeps a global id log and treats a
// repeated id as a carousel clone (renaming it), so reusing one id across opens
// would push us down that path for no reason.
let uid = 0;

/**
 * Works out the vendor and vendor video id from a watch/share URL.
 *
 * @param {string} url
 * @returns {?{vendor: string, videoId: string, src: string, allow: string}}
 */
function parseVideoUrl(url) {
    if (!url) return null;

    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
    if (yt) {
        const videoId = yt[1];
        // rel=0 + enablejsapi=1 match video_iframe; enablejsapi is what lets
        // YT.Player attach to an already-rendered iframe.
        let src = `https://www.youtube.com/embed/${videoId}?rel=0&enablejsapi=1`;
        if (window.location.origin) {
            src += `&origin=${encodeURIComponent(window.location.origin)}`;
        }
        // Starts muted so the modal's autoplay isn't blocked by the browser and
        // doesn't blast sound the moment it opens; oilib's own unmute control
        // (rendered because `controls` is in data-oi-video-has) lets the visitor
        // turn sound on.
        src += "&autoplay=1&mute=1";
        return { vendor: "youtube", videoId, src, allow: YOUTUBE_ALLOW };
    }

    // Unlisted Vimeo URLs carry a second hash slug (vimeo.com/<id>/<hash>) which
    // becomes an `h=` parameter on the player URL.
    const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/(\w+))?/);
    if (vimeo) {
        const videoId = vimeo[1];
        let src = `https://player.vimeo.com/video/${videoId}`;
        src += vimeo[2] ? `?h=${vimeo[2]}&autoplay=1&muted=1` : "?autoplay=1&muted=1";
        return { vendor: "vimeo", videoId, src, allow: VIMEO_ALLOW };
    }

    return null;
}

/**
 * Builds oilib-compatible player markup for a parsed video.
 *
 * @param {{vendor: string, videoId: string, src: string, allow: string}} video
 * @param {string} id - Unique element id for the iframe.
 * @param {string} title - Accessible title; pass "" to let oilib fill it from the vendor API.
 * @returns {string}
 */
function buildPlayerMarkup(video, id, title) {
    return (
        `<div class="oi-aspect sixteen-nine oi-video oi-video-${video.vendor} oi-video-uid-${id}" id="${id}-container">` +
        '<div class="oi-aspect-spacer"></div>' +
        `<iframe id="${id}"` +
        ' class="oi-video-iframe oi-aspect-target embed-responsive-item oi-fit-fill"' +
        ` data-oi-video-vendor="${video.vendor}"` +
        ` data-oi-video-vendor-video-id="${video.videoId}"` +
        ' data-oi-video-autoload="true"' +
        ' data-oi-video-has="controls,autoplay,mute"' +
        ` src="${video.src}"` +
        ' width="640" height="338" loading="eager"' +
        ` title="${title}"` +
        ` allow="${video.allow}"` +
        // Defined in oicms head_open.html. It only flags the iframe as loaded so
        // oilib can mount it whichever order the two finish in.
        ' onload="oiVideoOnLoad(this)"' +
        " frameborder=\"0\" allowfullscreen></iframe>" +
        "</div>"
    );
}

// oilib seeds a player's `is_playing` flag from the `autoplay` attribute the moment it
// mounts, whether or not the browser actually started playback (see hero_video.js, which
// hits the same issue for the home page hero slider) -- so the URL's autoplay=1/mute=1
// alone isn't reliable here either. Drive it explicitly once the player is ready.
function driveAutoplay(id, attempt = 1) {
    const video = window.oi && window.oi.loaded && window.oi.loaded.video && window.oi.loaded.video[id];
    const player = video && video.player;

    if (!player) {
        // Not mounted yet -- normal on first paint. oilib dispatches this once the vendor
        // API attaches (youtube.js / vimeo.js player_ready).
        window.addEventListener("oi.loaded.video." + id, () => driveAutoplay(id), { once: true });
        return;
    }

    try {
        if (video.vendor === "youtube" && player.playVideo) {
            if (player.mute) player.mute();
            player.playVideo();
        } else if (video.vendor === "vimeo" && player.play) {
            if (player.setMuted) player.setMuted(true);
            const result = player.play();
            if (result && result.catch) result.catch(() => {});
        }
        player.is_playing = true;
    } catch (e) {
        // A modal closed mid-mount tears the iframe down with it; nothing left to drive.
        return;
    }

    // Vimeo's play() can resolve without ever actually starting. Verify and retry a
    // couple of times before giving up, same as hero_video.js's play().
    if (attempt >= 3 || !player.getCurrentTime) return;

    Promise.resolve(player.getCurrentTime())
        .catch(() => null)
        .then((before) => {
            window.setTimeout(() => {
                Promise.resolve(player.getCurrentTime())
                    .catch(() => null)
                    .then((after) => {
                        const moving = before !== null && after !== null && after > before;
                        if (!moving) driveAutoplay(id, attempt + 1);
                    });
            }, 700);
        });
}

document.querySelectorAll(".oi-video-modal").forEach((modal) => {
    // Both modals in the theme name their body `<modal id>-body`.
    const body = document.getElementById(`${modal.id}-body`);
    if (!body) return;

    let activeId = null;

    modal.addEventListener("show.bs.modal", (e) => {
        const trigger = e.relatedTarget;
        const video = parseVideoUrl(trigger ? trigger.dataset.video : "");

        if (!video) {
            body.innerHTML = "";
            activeId = null;
            return;
        }

        activeId = `oi-video-modal-${++uid}`;
        // An empty title lets oilib substitute the real one from the vendor API;
        // prefer an explicit label from the trigger when there is one.
        const title = (trigger && (trigger.dataset.videoTitle || trigger.getAttribute("aria-label"))) || "";
        body.innerHTML = buildPlayerMarkup(video, activeId, title);
        driveAutoplay(activeId);
    });

    modal.addEventListener("hidden.bs.modal", () => {
        // Pause through oilib first so it can flush progress state for the player
        // it is tracking, then drop the iframe.
        if (activeId && window.oi && window.oi.video) {
            window.oi.video.pause(activeId);
        }
        body.innerHTML = "";
        activeId = null;
    });
});
